import { curryAppSelector } from "@/store/reselect";
import { GUID } from "@faro-lotv/foundation";
import { createSelector } from "@reduxjs/toolkit";
import { selectScanMarkersVisibilityMode } from "../data-preparation-view-options/data-preparation-view-options-selectors";
import { ScanMarkersVisibilityMode } from "../data-preparation-view-options/data-preparation-view-options-slice";
import {
  selectRevisionEntityChildrenMap,
  selectRevisionEntityMap,
  selectRootEntity,
} from "../revision/revision-entity-selectors";
import { getAncestorIds, getDescendantIds } from "../revision/selector-helpers";
import { selectVisibilityMap } from "./data-preparation-ui-selectors";

/**
 * @param state The current application state.
 * @param entityId The ID of the entity to determine the visibility of.
 * @returns `true` if the user set the entity itself to be visible, `false` if it is set to hidden.
 *  Note that this selector doesn't consider the visibility of ancestor entities like clusters, but only the entity itself.
 */
export const selectIsEntitySetToVisible = curryAppSelector(
  createSelector(
    [selectVisibilityMap, (state, entityId: GUID) => entityId],
    (visibilityMap, entityId) => visibilityMap[entityId] !== false,
  ),
);

/**
 * @param state The current application state.
 * @returns true if root scan is in visibility map, false otherwise
 */
export const selectRootVisibility = createSelector(
  [selectRootEntity, selectVisibilityMap],
  (rootEntity, visibilityMap) =>
    rootEntity === undefined ? false : visibilityMap[rootEntity.id] !== false,
);

/**
 * @param state The current application state.
 * @param entityId entity id for which the visibility is toggled to visible
 * @returns All affected entity ids in the revision when given entity is set to visible(true)
 */
export const selectAffectedEntitiesOnVisibilityToggleTrue = curryAppSelector(
  createSelector(
    [
      selectRevisionEntityMap,
      selectRevisionEntityChildrenMap,
      (state, entityId: GUID) => entityId,
    ],
    (entityMap, childrenMap, entityId) => {
      const descendantIds = getDescendantIds(childrenMap, entityId);
      const ancestorIds = getAncestorIds(entityMap, entityId);
      return [...new Set([...descendantIds, ...ancestorIds])];
    },
  ),
);

/**
 * @param state The current application state.
 * @param entityId entity id for which the visibility is toggled to non-visible
 * @returns All affected entity ids in the revision when the given entity id is set to non-visible(false)
 */
export const selectAffectedEntitiesOnVisibilityToggleFalse = curryAppSelector(
  createSelector(
    [
      selectRevisionEntityMap,
      selectRevisionEntityChildrenMap,
      selectVisibilityMap,
      (state, entityId: GUID) => entityId,
    ],
    (entityMap, childrenMap, visibilityMap, entityId) => {
      const descendantIds = getDescendantIds(childrenMap, entityId);
      // All the descendants and self should be made non-visible (descendant ids will also include self)
      const affectedEntityIds: GUID[] = [...descendantIds];

      // Remove the first element which is self
      const ancestorIds = getAncestorIds(entityMap, entityId).slice(1);

      ancestorIds.forEach((id) => {
        const childEntities = getDescendantIds(childrenMap, id).slice(1);
        const allNonVisible = childEntities.every(
          (id) => visibilityMap[id] === false || affectedEntityIds.includes(id),
        );
        if (allNonVisible) {
          affectedEntityIds.push(id);
        }
      });

      return affectedEntityIds;
    },
  ),
);

/**
 * @param state The current application state.
 * @param scanEntityId The scan id to check its corresponding marker visibility.
 * @returns `true` if the scan marker is visible, false otherwise.
 */
export const selectIsScanMarkerVisible = curryAppSelector(
  createSelector(
    [
      (state, scanEntityId: GUID) =>
        selectIsEntitySetToVisible(scanEntityId)(state),
      selectScanMarkersVisibilityMode,
    ],
    (isScanVisible, scanMarkersVisibilityMode) => {
      switch (scanMarkersVisibilityMode) {
        case ScanMarkersVisibilityMode.showAll:
          return true;
        case ScanMarkersVisibilityMode.showNone:
          return false;
        case ScanMarkersVisibilityMode.showVisible:
          return isScanVisible;
      }
    },
  ),
);
